import { autocompleteClasses } from '@mui/material/Autocomplete';
import { v2CheckListboxClass } from './menu-item-modifiers';
/** Gap between a trigger field and the dropdown it opens. A number because Popper offsets take px. */
export const DROPDOWN_TRIGGER_GAP_PX = 4;
export const DROPDOWN_LIST_PADDING = '0.375rem';
export const DROPDOWN_OPTION_PADDING_X = '0.5625rem';
/** Size of a trigger's trailing chevron and clear glyphs. */
export const DROPDOWN_INDICATOR_SIZE = '1rem';
/** Distance from a trigger's right edge to its chevron (SelectTrigger's right padding). */
export const DROPDOWN_INDICATOR_INSET = '0.5625rem';
/** Base surface for a V2 dropdown Paper (SelectMenu's Popover, AppMenu's Menu, the MuiMenu theme override, …). */
export const getDropdownSurfaceBackground = (theme) => theme.palette.mode === 'dark'
    ? theme.palette.custom.OV.sys.surfaceContainer
    : theme.palette.custom.OV.sys.surfaceContainerLowest;
/** Hover/selected row background for a V2 dropdown menu item. */
export const getDropdownSurfaceActiveBackground = (theme) => theme.palette.mode === 'dark'
    ? theme.palette.custom.OV.sys.surfaceContainerHighest
    : theme.palette.custom.OV.sys.surfaceContainer;
/** The card every dropdown sits on: SelectMenu, MUI Menu/Select, Autocomplete. */
export const getDropdownPaperStyles = (theme) => ({
    backgroundColor: getDropdownSurfaceBackground(theme),
    // Paper paints a white elevation overlay over the token in dark mode,
    // which would lift this surface above the other dropdowns'.
    backgroundImage: 'none',
    border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
    borderRadius: '0.75rem',
    boxShadow: theme.shadows[8],
});
/** Geometry of one dropdown row; the highlight comes from `getDropdownSurfaceActiveBackground`. */
export const getDropdownOptionStyles = () => ({
    gap: '0.625rem',
    padding: `0.5rem ${DROPDOWN_OPTION_PADDING_X}`,
    borderRadius: '0.5rem',
    cursor: 'pointer',
});
export const getDropdownOptionLabelStyles = (theme) => ({
    fontSize: '0.78125rem',
    fontWeight: 600,
    lineHeight: '1.125rem',
    color: theme.palette.custom.OV.sys.onBackground,
});
export const getDropdownGroupHeadingStyles = (theme) => ({
    // The negative `top` cancels the list's padding so rows can't peek above a pinned heading.
    position: 'sticky',
    top: `-${DROPDOWN_LIST_PADDING}`,
    zIndex: 1,
    padding: `0.5rem ${DROPDOWN_OPTION_PADDING_X} 0.125rem`,
    backgroundColor: getDropdownSurfaceBackground(theme),
    fontSize: '0.6875rem',
    fontWeight: 600,
    lineHeight: '1rem',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
/** A trigger's trailing glyph slot (chevron, clear), sized and coloured like SelectTrigger's. */
export const getDropdownIndicatorStyles = (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    '& svg': { width: DROPDOWN_INDICATOR_SIZE, height: DROPDOWN_INDICATOR_SIZE },
});
/** The clear glyph darkens on hover; callers decide when it is shown. */
export const getDropdownClearIndicatorStyles = (theme) => ({
    ...getDropdownIndicatorStyles(theme),
    width: DROPDOWN_INDICATOR_SIZE,
    height: DROPDOWN_INDICATOR_SIZE,
    borderRadius: '50%',
    transition: 'opacity 120ms ease, color 120ms ease',
    '&:hover': {
        color: theme.palette.custom.OV.sys.onBackground,
        backgroundColor: 'transparent',
    },
});
/** MUI Select's value box restyled as a SelectTrigger: lucide chevron that never flips, clear on hover. */
export const getSelectTriggerRootStyles = (theme) => {
    const gap = theme.spacing(1);
    const clearButton = '.MuiInputAdornment-positionEnd .MuiIconButton-root';
    return {
        '&.MuiInputBase-adornedEnd': {
            paddingRight: `calc(${DROPDOWN_INDICATOR_INSET} + ${DROPDOWN_INDICATOR_SIZE} + ${gap})`,
        },
        // Outranks MUI's `&&&` right padding, which keeps room for the chevron the root now pads for.
        '& .MuiSelect-select.MuiInputBase-input:has(~ .MuiInputAdornment-positionEnd)': {
            paddingRight: 0,
        },
        '& .MuiInputAdornment-positionEnd': { marginLeft: gap, marginRight: 0 },
        [`& ${clearButton}`]: {
            ...getDropdownClearIndicatorStyles(theme),
            padding: 0,
            opacity: 0,
            '& .MuiTouchRipple-root': { display: 'none' },
            '@media (hover: none)': { opacity: 1 },
        },
        [`&:hover ${clearButton}, &.Mui-focused ${clearButton}`]: {
            opacity: 1,
        },
    };
};
export const getSelectTriggerIconStyles = (theme) => ({
    width: DROPDOWN_INDICATOR_SIZE,
    height: DROPDOWN_INDICATOR_SIZE,
    top: `calc(50% - ${DROPDOWN_INDICATOR_SIZE} / 2)`,
    right: DROPDOWN_INDICATOR_INSET,
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
export const getAutocompleteIndicatorStyles = (theme) => {
    const iconButtonReset = {
        padding: 0,
        marginRight: 0,
        '&:hover': { backgroundColor: 'transparent' },
        '& .MuiTouchRipple-root': { display: 'none' },
    };
    return {
        endAdornment: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
        popupIndicator: { ...iconButtonReset, ...getDropdownIndicatorStyles(theme) },
        clearIndicator: {
            ...iconButtonReset,
            ...getDropdownClearIndicatorStyles(theme),
            '@media (hover: none)': { visibility: 'visible' },
        },
    };
};
/** Loading / empty / load-more line inside a dropdown list. */
export const getDropdownStatusStyles = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8125rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
});
/**
 * MUI Autocomplete's listbox restyled as a SelectMenu list. Only a listbox marked with
 * `v2CheckListboxClass` trades the selected tint for a check; elsewhere the tint is the only cue.
 */
export const getAutocompleteDropdownListboxStyles = (theme) => {
    const activeBackground = getDropdownSurfaceActiveBackground(theme);
    const highlightedSelector = `&.${autocompleteClasses.focused}, &.${autocompleteClasses.focusVisible}`;
    return {
        padding: DROPDOWN_LIST_PADDING,
        [`& .${autocompleteClasses.option}`]: {
            ...getDropdownOptionStyles(),
            ...getDropdownOptionLabelStyles(theme),
            minHeight: 'unset',
            '& .MuiTypography-root': {
                flex: '1 1 auto',
                minWidth: 0,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
            },
            [highlightedSelector]: { backgroundColor: activeBackground },
        },
        [`&.${v2CheckListboxClass} .${autocompleteClasses.option}[aria-selected="true"]`]: {
            backgroundColor: 'transparent',
            [highlightedSelector]: { backgroundColor: activeBackground },
        },
    };
};
